import {
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconCheck, IconEye } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DECK_CARD_FACES,
  DECK_CARD_FACE_LABELS,
  DEMO_SOLO_TASK_REWARDS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  type EventCardAward,
  type GameDrawnCardView,
  type GameParticipantView,
  type ResourceKey,
} from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';

const ALL = 'all';

/**
 * «Таланты · карта 38» — как в МАК-сессиях. Номер печатный: по нему ведущий
 * находит трактовку карты в инструкции. У карты рубашкой вверх номера нет
 * (`HiddenCardView`) — он выдал бы её до переворота.
 */
const cardCaption = (card: GameDrawnCardView): string | null =>
  card.card.title ??
  (card.card.position === null ? null : `${card.deckTitle} · карта ${card.card.position}`);

/**
 * Where the table's cards are read.
 *
 * Two filters, because two different questions are being asked: «чьи это карты»
 * and «образ или текст». The defaults differ by role — a facilitator runs the
 * whole table and needs everything at once, a player works with their own
 * imagery and reaches for the text only when the round calls for it.
 */
export function GameCardsPanel({
  cards,
  players,
  viewerParticipantId,
  facilitator,
  busy,
  onReveal,
  onResolve,
  onConfirm,
  recommendAward,
  allFaces = false,
  onModalChange,
  modalAction,
}: {
  cards: GameDrawnCardView[];
  players: GameParticipantView[];
  viewerParticipantId?: string;
  facilitator: boolean;
  busy: boolean;
  onReveal: (cardId: string) => void;
  /** GAME-39 — ведущий закрывает карту события после ответа участника. */
  onResolve?: (cardId: string, award: EventCardAward) => void;
  /** Одиночное демо: игрок сам отмечает, что задание его карты выполнено. */
  onConfirm?: (cardId: string) => void;
  /** Демо: ресурс, который сектор даёт за ответ по правилам, — первым в меню ведущего. */
  recommendAward?: (card: GameDrawnCardView) => ResourceKey | undefined;
  /** Демо: карты Дракона и Ролей — текстовые, и игроку они нужны сразу. */
  allFaces?: boolean;
  /**
   * Открыто ли сейчас окно карты. Сообщается сразу, в момент открытия, а не
   * эффектом позже: по этому признаку итог демо ждёт, пока человек
   * рассмотрит последнюю карту, и не ложится поверх неё.
   */
  onModalChange?: (open: boolean) => void;
  /** Кнопка в окне открытой карты — в демо после финиша «Перейти к итогу». */
  modalAction?: { label: string; onClick: () => void } | null;
}) {
  /*
    The card is held by id, not by value: a face-down card is revealed by a round
    trip to the server, and the open modal has to show the face the moment the
    snapshot comes back rather than the copy it was opened with.
  */
  const [openId, setOpenIdState] = useState<string | null>(null);
  const setOpenId = (id: string | null) => {
    setOpenIdState(id);
    onModalChange?.(id !== null);
  };
  const shown = useRef<Set<string> | null>(null);
  const [owner, setOwner] = useState<string>(
    facilitator || !viewerParticipantId ? ALL : viewerParticipantId,
  );
  const [face, setFace] = useState<string>(facilitator || allFaces ? ALL : 'imagery');

  const ownerOptions = useMemo(() => {
    if (!facilitator && viewerParticipantId) {
      return [
        { value: viewerParticipantId, label: 'Мои' },
        { value: ALL, label: 'Все карты партии' },
      ];
    }
    return [
      { value: ALL, label: 'Все' },
      ...players.map((player) => ({ value: player.id, label: player.displayName })),
    ];
  }, [facilitator, players, viewerParticipantId]);

  /*
    A drawn card lands on the table by itself, for everyone at once — that is
    what «карту видят все участники» means. A face-down card only reaches the
    player who drew it and the facilitator; for the rest it appears at the moment
    it is turned over, and their modal opens then. One card at a time: a «Т» cell
    draws two, and they are looked at one after another, not stacked.
  */
  useEffect(() => {
    if (shown.current === null) {
      // On mount the panel is a history — replaying it as a stack of modals is not a welcome.
      shown.current = new Set(cards.map((card) => card.id));
      return;
    }
    if (openId !== null) return;
    const fresh = cards.find((card) => !shown.current?.has(card.id));
    if (!fresh) return;
    shown.current.add(fresh.id);
    setOpenId(fresh.id);
  }, [cards, openId]);

  const openCard = openId ? cards.find((card) => card.id === openId) ?? null : null;
  const openCaption = openCard ? cardCaption(openCard) : null;
  const mayReveal = (card: GameDrawnCardView) =>
    facilitator || card.drawnByParticipantId === viewerParticipantId;
  const mayConfirm = (card: GameDrawnCardView) =>
    Boolean(onConfirm) && card.awaitingResolution && card.revealed &&
    card.drawnByParticipantId === viewerParticipantId;
  const confirmLabel = (card: GameDrawnCardView) => {
    const reward = card.sectorId ? DEMO_SOLO_TASK_REWARDS[card.sectorId] : undefined;
    return reward ? `Задание выполнено · +1 ${RESOURCE_LABELS[reward]}` : 'Задание выполнено';
  };

  /* Closing lets the next card of the same throw take the table. */
  const closeCard = () => {
    const next = cards.find((card) => !shown.current?.has(card.id));
    if (next) {
      shown.current?.add(next.id);
      setOpenId(next.id);
      return;
    }
    setOpenId(null);
  };

  const visible = cards.filter((card) =>
    (owner === ALL || card.drawnByParticipantId === owner) &&
    (face === ALL || card.cardFace === face));

  const filtered = visible.length !== cards.length;

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between" gap="sm" wrap="wrap">
          <Group gap="sm" wrap="wrap">
            <Text fw={700}>Карты партии</Text>
            <Select
              size="xs"
              w={180}
              data={ownerOptions}
              value={owner}
              onChange={(next) => setOwner(next ?? ALL)}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              aria-label="Чьи карты показывать"
            />
            <SegmentedControl
              size="xs"
              value={face}
              onChange={setFace}
              data={[
                ...DECK_CARD_FACES.map((value) => ({
                  value,
                  label: DECK_CARD_FACE_LABELS[value],
                })),
                { value: ALL, label: 'Все' },
              ]}
              aria-label="Образы или текст"
            />
          </Group>
          {/* Filtered, a bare number would read as «это все карты». */}
          <Text size="xs" c="var(--text-muted)" data-testid="cards-count">
            {filtered ? `${visible.length} из ${cards.length}` : cards.length}
          </Text>
        </Group>

        {cards.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">Карты ещё не вытягивали.</Text>
        ) : visible.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">Под этот отбор карт нет.</Text>
        ) : (
          <ScrollArea type="auto">
            <Group wrap="nowrap" align="flex-start" pb="xs">
              {visible.map((card) => (
                <Stack key={card.id} gap={6} align="center">
                  <PlayingCard
                    frontImage={card.card.frontImage}
                    backImage={card.card.backImage}
                    faceUp={card.revealed}
                    caption={card.isShared ? 'Общая' : undefined}
                    label={card.revealed
                      ? `Открыть крупно: ${cardCaption(card) ?? card.deckTitle}`
                      : `Открыть карту: ${card.deckTitle}`}
                    width={98}
                    disabled={busy}
                    onClick={() => card.revealed ? setOpenId(card.id) : onReveal(card.id)}
                  />
                  {/* Номер под каждой открытой картой — чтобы не открывать их по одной. */}
                  {card.revealed && card.card.position !== null ? (
                    <Text size="xs" c="var(--text-muted)" data-testid={`card-number-${card.id}`}>
                      карта {card.card.position}
                    </Text>
                  ) : null}
                  {!card.revealed ? (
                    <Button
                      size="compact-xs"
                      variant="light"
                      leftSection={<IconEye size={13} />}
                      disabled={busy}
                      onClick={() => onReveal(card.id)}
                    >
                      Открыть
                    </Button>
                  ) : card.awaitingResolution ? (
                    /*
                      GAME-39: открытая карта ещё не отработана. Участник
                      отвечает голосом, ведущий начисляет — до этого ход стоит,
                      поэтому статус видно и участнику, а кнопка есть у ведущего.
                      В одиночном демо ведущего нет: выполнение отмечает игрок.
                    */
                    mayConfirm(card) ? (
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="gold"
                        leftSection={<IconCheck size={13} />}
                        disabled={busy}
                        onClick={() => onConfirm?.(card.id)}
                      >
                        Выполнено
                      </Button>
                    ) : facilitator && onResolve ? (
                      <AwardMenu
                        busy={busy}
                        recommended={recommendAward?.(card)}
                        onAward={(award) => onResolve(card.id, award)}
                      />
                    ) : (
                      <Badge size="xs" color="gold" variant="light">Ждём ведущего</Badge>
                    )
                  ) : null}
                </Stack>
              ))}
            </Group>
          </ScrollArea>
        )}
      </Stack>

      <Modal
        opened={Boolean(openCard)}
        onClose={closeCard}
        title={openCard?.deckTitle}
        centered
        size="auto"
      >
        {openCard ? (
          <Stack align="center" gap="md">
            <PlayingCard
              frontImage={openCard.card.frontImage}
              backImage={openCard.card.backImage}
              faceUp={openCard.revealed}
              label={openCard.revealed
                ? (openCaption ?? openCard.deckTitle)
                : `Рубашка: ${openCard.deckTitle}`}
              width={320}
              disabled={busy || openCard.revealed || !mayReveal(openCard)}
              onClick={openCard.revealed ? undefined : () => onReveal(openCard.id)}
            />
            {openCard.revealed ? (
              <Stack gap={4} align="center">
                {openCaption ? (
                  <Text fw={600} data-testid="open-card-caption">{openCaption}</Text>
                ) : null}
                <Text size="sm" c="var(--text-muted)">
                  {onConfirm
                    ? 'Карта останется в «Картах партии».'
                    : 'Карту видят все участники. Она останется в «Картах партии».'}
                </Text>
                {modalAction ? (
                  <Button
                    mt="xs"
                    onClick={() => {
                      /* Остальные карты — в «Картах партии»; сейчас время итога. */
                      for (const card of cards) shown.current?.add(card.id);
                      setOpenId(null);
                      modalAction.onClick();
                    }}
                    data-testid="card-modal-action"
                  >
                    {modalAction.label}
                  </Button>
                ) : null}
                {mayConfirm(openCard) ? (
                  <Stack gap={6} align="center" mt="xs" maw={340}>
                    <Text size="sm" ta="center">
                      Ответьте на вопросы карты вслух или для себя. Ответ никуда не отправляется.
                    </Text>
                    <Button
                      color="gold"
                      leftSection={<IconCheck size={16} />}
                      disabled={busy}
                      onClick={() => {
                        onConfirm?.(openCard.id);
                        closeCard();
                      }}
                      data-testid="confirm-task"
                    >
                      {confirmLabel(openCard)}
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
            ) : mayReveal(openCard) ? (
              <Button
                leftSection={<IconEye size={16} />}
                disabled={busy}
                onClick={() => onReveal(openCard.id)}
              >
                Открыть
              </Button>
            ) : (
              <Text size="sm" c="var(--text-muted)">
                Карту открывает тот, кто её вытянул.
              </Text>
            )}
          </Stack>
        ) : null}
      </Modal>
    </Paper>
  );
}

/**
 * What the facilitator gives for an answered card (GAME-39).
 *
 * A menu rather than a single button, because the card names the reward and it
 * is not always the same one: a sector card may promise a resource, a Bazaar
 * card a Talent «от вселенной». «Без начисления» is here because Павел is
 * right that there is a variation, and a session must not stall on a card that
 * ends in nothing.
 */
export function AwardMenu({
  busy,
  onAward,
  recommended,
  size = 'compact-xs',
}: {
  busy: boolean;
  onAward: (award: EventCardAward) => void;
  /** Демо: ресурс, положенный по правилам сектора, — первым и с пометкой. */
  recommended?: ResourceKey;
  size?: 'compact-xs' | 'md';
}) {
  const ordered = recommended
    ? [recommended, ...RESOURCE_KEYS.filter((resource) => resource !== recommended)]
    : [...RESOURCE_KEYS];
  /*
    Одно решение на одно открытие меню. Пункт успевал сработать дважды при
    двойном нажатии, пока меню закрывалось: сервер второй запрос отклонял, а
    человек видел ошибку «уже завершено». Снова выбрать можно, открыв меню.
  */
  const sent = useRef(false);
  const award = (value: EventCardAward) => {
    if (sent.current) return;
    sent.current = true;
    onAward(value);
  };
  return (
    <Menu shadow="md" withinPortal position="bottom" onOpen={() => { sent.current = false; }}>
      <Menu.Target>
        <Button
          size={size}
          variant={size === 'md' ? 'filled' : 'light'}
          color="gold"
          leftSection={<IconCheck size={size === 'md' ? 18 : 13} />}
          disabled={busy}
          data-testid="award-menu"
        >
          Ответ принят
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Начислить за ответ</Menu.Label>
        {ordered.map((resource) => (
          <Menu.Item
            key={resource}
            onClick={() => award({ kind: 'resource', resource })}
          >
            +1 {RESOURCE_LABELS[resource]}
            {resource === recommended ? ' — по правилам сектора' : ''}
          </Menu.Item>
        ))}
        <Menu.Item onClick={() => award({ kind: 'talent' })}>
          Карта Таланта
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item onClick={() => award({ kind: 'none' })}>
          Без начисления
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
